/**
 * Particle effects for the board.
 *
 * The effects themselves are not written here. Each one is a plain
 * `EffectDef` JSON file under `src/effects/`, authored for — and re-openable
 * in — the studio at https://github.com/andywise/vfx-studio ("Open file…"),
 * and played back by that project's runtime, vendored into
 * `src/vendor/vfx-runtime/`. This module is only the wiring: where each effect
 * is spawned, which way up, how big, and when it stops.
 *
 * ---------------------------------------------------------------------------
 * The one convention that matters: +Y is out of the face
 * ---------------------------------------------------------------------------
 * The runtime authors effects in a Y-up frame — cones open along +Y, rings and
 * boxes lie in XZ, gravity pulls toward -Y. Nothing on this board is Y-up: the
 * three playable faces are steep planes at 120 degrees to each other. So every
 * spawn carries a quaternion, and the deal is:
 *
 *     +Y  -> the face's outward normal      ("up", off the stonework)
 *     XZ  -> the plane of the face          (where rings and slabs lie)
 *
 * which means an effect authored to sit on the ground in the studio sits on
 * the *face* here, whichever way the pyramid happens to be turned. Positive
 * gravity settles debris back onto the slope; negative gravity lifts motes off
 * it.
 *
 * Which is why a *falling* effect cannot use plain gravity. On the face turned
 * to the camera, local -Y points into the screen, so that module presses
 * particles into the stonework rather than dropping them. Gravity that should
 * read as weight carries `"space": "world"` instead — scene -Y, resolved back
 * into the emitter's frame each frame so it survives the board being turned
 * mid-flight. That is what the fireworks and the shatter debris fall with; their
 * drag is what still holds them near the slope they came off.
 *
 * Edge effects get a second frame, because a border needs a direction as well
 * as a plane:
 *
 *     X   -> along the edge                 (the `edge` emitter's line)
 *     +Y  -> in the face, away from the shape ("outward", where the glow goes)
 *     Z   -> the face normal
 *
 * Every cell is a unit triangle, so every outline edge is exactly 1 board unit
 * and the edge emitters are all authored `length: 1` — no per-edge scaling.
 *
 * Two effects opt out, and both for the same reason: what they draw does not
 * belong to a face. A rocket goes up, off the ground and past the board, and the
 * beam's sparks go up out of the tip — so `combo()` and `apexBurst()` aim theirs
 * with `#skywardQuaternion`, +Y along the *scene's* up, and place them on the base
 * plane and on the apex rather than on a cell. They are the two files under
 * `effects/` authored genuinely Y-up.
 *
 * The fireworks' launch geometry, and the window their burst has to land in, live
 * in `fireworks.js` — shared with `verify-effects`, which is what keeps the
 * detonation over the tip of the pyramid rather than off the top of the screen.
 * The beam's own column is not here at all: it is a shader on one quad, in
 * `beam.js`, and these particles are only what comes off it.
 *
 * ---------------------------------------------------------------------------
 * Tinting
 * ---------------------------------------------------------------------------
 * Effect JSON carries fixed colours, and `spawn()` has no colour argument, so
 * an effect that has to wear a gem's tone is registered once per tone with its
 * birth colours pre-multiplied. Which layers take the tone is declared in the
 * JSON rather than here: **a layer whose name ends in `(tinted)` gets tinted**,
 * everything else keeps the colour it was authored with. Rename a layer in the
 * studio and this follows — that is the point of putting the marker there.
 *
 * The whole system hangs off the pyramid group, so effects are placed in board
 * coordinates (cell centroids, straight from `geometry.js`) and ride the
 * board's rotation for free — a clear that goes off while you spin keeps its
 * particles on the face they belong to.
 */

import { Matrix4, Quaternion, Vector3 } from 'three/webgpu';

import { VFXSystem } from './vendor/vfx-runtime/index.js';
import { APEX, CELLS, CELL_COUNT, cellCentroid, faceBasis, outlineEdges } from './geometry.js';
import {
  FAN,
  FAN_JITTER,
  GROUND_Y,
  LAUNCH_STANDOFF,
  PAD_GAP,
  STAGGER_MS,
} from './fireworks.js';
import { GEM_TONES, TONE_COUNT, fireworkToneSequence } from './palette.js';

import ambientFirefliesDef from './effects/ambient-fireflies.json';
import apexBeamDef from './effects/apex-beam.json';
import clearShatterDef from './effects/clear-shatter.json';
import comboFireworksDef from './effects/combo-fireworks.json';
import formationEdgeGlowDef from './effects/formation-edge-glow.json';
import gemSparkleDef from './effects/gem-sparkle.json';
import hoverAuroraDef from './effects/hover-aurora.json';
import hoverEdgeGlowDef from './effects/hover-edge-glow.json';
import pieceLandPoofDef from './effects/piece-land-poof.json';

/** Layers whose name ends with this wear the gameplay tone. See the header. */
const TINT_MARKER = '(tinted)';

/**
 * How far off the stonework effects are spawned. Enough to clear the crown of
 * a stone, so a sparkle reads as light coming off the gem rather than out of
 * the middle of it.
 */
const FACE_LIFT = 0.09;
/** Edge effects sit closer in: their whole job is to trace the outline. */
const EDGE_LIFT = 0.05;
/**
 * How far above the tip the beam's sparks are spawned. Just clear of the point,
 * so the flash reads as light leaving the pyramid rather than as a lamp buried
 * in the last course of stone.
 */
const APEX_LIFT = 0.06;

/**
 * Gap between one gem's sparkles and its next. Each stone keeps its own clock
 * with its own random phase, so the board twinkles rather than pulsing in
 * time — a shared timer is instantly readable as a machine.
 */
const SPARKLE_MIN_MS = 2100;
const SPARKLE_MAX_MS = 6400;

/**
 * A combo worth setting fireworks off for — which is any combo at all. Exported
 * because the sound has to agree with the picture: `main.js` fires the shells off
 * the same threshold, and a bang with no firework over it is worse than neither.
 */
export const FIREWORK_COMBO = 2;


/**
 * Deep jewel tones are the right colour for a faceted stone lit from outside
 * and the wrong one for light being emitted: emerald at its authored value is
 * nearly black, and an additive particle wearing it disappears against the
 * board. So the tone is opened up before it is used as light — but *how* turns
 * out to matter more than how far.
 *
 * Lerping toward white is the obvious move and it is the wrong one. It raises
 * the dim channels far more than the bright one in absolute terms, which is the
 * definition of washing a colour out: sapphire's red climbs three times as fast
 * as its blue. Push the result past 1 for the blend, run it through ACES, and
 * every tone converges on the same white — which is what the sparkles were
 * doing. Additive layers instead take the tone to **full saturation** first
 * (scale so the brightest channel reaches 1, which is a pure brightness change
 * and leaves hue and saturation exactly alone) and only then get a small lift
 * and the gain. A ruby sparkle comes out red at any brightness.
 *
 * Alpha layers saturate too, and it took a while to see why they should. The
 * worry is headroom: they have none, and every channel over 1 clips. But
 * saturating cannot clip — it *divides* by the brightest channel, so the peak
 * lands exactly on 1 by construction and nothing can exceed it. What was actually
 * washing them out was the lift, which raises the dim channels and leaves the
 * bright one alone: on emerald that is most of the colour gone. So the lift comes
 * most of the way off and the saturation goes on, which is what took the shatter
 * debris from pastel confetti to gem-coloured. They still take no gain — their job
 * is to look like the stone, not like light coming off it.
 *
 * `gem shards` is the only alpha-tinted layer in the game, so this pair of numbers
 * is, in practice, the colour of the confetti.
 */
const TINT = {
  additive: { saturate: true, lift: 0.12, gain: 1.35 },
  alpha: { saturate: true, lift: 0.04, gain: 1 },
};

const scratchMatrix = new Matrix4();
const scratchX = new Vector3();
const scratchY = new Vector3();
const scratchZ = new Vector3();
const scratchPos = new Vector3();
const scratchScale = new Vector3();
const scratchQuat = new Quaternion();

const isTinted = (layer) => layer.name?.trimEnd().endsWith(TINT_MARKER);

/** A palette entry, opened up enough to survive `blend`. See TINT. */
function toneRgb(tone, blend) {
  const hex = GEM_TONES[(((tone ?? 0) % TONE_COUNT) + TONE_COUNT) % TONE_COUNT];
  const base = [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
  const { saturate, lift, gain } = TINT[blend] ?? TINT.additive;

  // Scaling by the brightest channel is a pure brightness change: ratios
  // between channels are untouched, so hue and saturation come through exactly.
  const peak = saturate ? Math.max(...base) || 1 : 1;
  return base.map((c) => ((c / peak) + (1 - c / peak) * lift) * gain);
}

/** Multiply a birth-colour provider's RGB by `rgb`, leaving alpha alone. */
function tintColorProvider(provider, rgb) {
  const apply = (rgba) => [rgba[0] * rgb[0], rgba[1] * rgb[1], rgba[2] * rgb[2], rgba[3]];
  switch (provider?.type) {
    case 'constant':
      return { ...provider, value: apply(provider.value) };
    case 'randomBetween':
      return { ...provider, a: apply(provider.a), b: apply(provider.b) };
    case 'gradient':
      return {
        ...provider,
        gradient: {
          ...provider.gradient,
          stops: provider.gradient.stops.map((s) => ({ ...s, color: apply(s.color) })),
        },
      };
    default:
      return provider;
  }
}

/** A copy of `def` with every `(tinted)` layer's birth colour wearing `tone`. */
function tintDef(def, tone) {
  return {
    ...def,
    meta: { ...def.meta, name: `${def.meta.name} · tone ${tone}` },
    layers: def.layers.map((layer) =>
      isTinted(layer)
        ? {
            ...layer,
            emitter: {
              ...layer.emitter,
              initial: {
                ...layer.emitter.initial,
                color: tintColorProvider(
                  layer.emitter.initial.color,
                  toneRgb(tone, layer.material?.blend),
                ),
              },
            },
          }
        : layer,
    ),
  };
}

/**
 * One registered effect, plus the per-tone variants of it if its JSON asks for
 * any. Callers always pass a tone and never have to know which effects care.
 */
class Effect {
  constructor(system, def, opts) {
    this.tinted = def.layers.some(isTinted);
    this.variants = this.tinted
      ? GEM_TONES.map((_, tone) => system.register(tintDef(def, tone), opts))
      : [system.register(def, opts)];
  }

  spawn(opts, tone = 0) {
    const index = this.tinted ? ((tone % TONE_COUNT) + TONE_COUNT) % TONE_COUNT : 0;
    return this.variants[index].spawn(opts);
  }
}

/**
 * +Y along the face's outward normal, XZ in the plane of the face.
 *
 * Built from the face's own basis rather than a shortest-arc rotation from
 * +Y, so the effect's X and Z axes land on the face's own axes and stay put
 * as the board turns instead of rolling about the normal.
 */
function faceQuaternion(face) {
  const basis = faceBasis(face);
  scratchX.set(...basis.right);
  scratchY.set(...basis.normal);
  scratchZ.crossVectors(scratchX, scratchY);
  return new Quaternion().setFromRotationMatrix(
    scratchMatrix.makeBasis(scratchX, scratchY, scratchZ),
  );
}

/** X along the edge, +Y outward in the face — the frame edge emitters want. */
function edgeQuaternion(edge) {
  scratchX.set(...edge.along);
  scratchY.set(...edge.outward);
  scratchZ.crossVectors(scratchX, scratchY);
  return new Quaternion().setFromRotationMatrix(
    scratchMatrix.makeBasis(scratchX, scratchY, scratchZ),
  );
}

/** A point lifted clear of the face it sits on. */
function lifted(point, normal, by) {
  return [point[0] + normal[0] * by, point[1] + normal[1] * by, point[2] + normal[2] * by];
}

/** Centroid of a set of cells, and how far across the set reaches. */
function cellsExtent(cells) {
  const centre = [0, 0, 0];
  for (const id of cells) {
    const c = cellCentroid(id);
    for (let k = 0; k < 3; k += 1) centre[k] += c[k] / cells.length;
  }
  let radius = 0;
  for (const id of cells) {
    const c = cellCentroid(id);
    radius = Math.max(radius, Math.hypot(c[0] - centre[0], c[1] - centre[1], c[2] - centre[2]));
  }
  return { centre, radius };
}

export class BoardVFX {
  /** @param {import('three/webgpu').Object3D} parent the pyramid group */
  constructor(parent) {
    /** Kept for `#skywardQuaternion`, which needs the board's world rotation. */
    this.parent = parent;
    this.system = new VFXSystem(parent);

    /** Bumped by `reset()`; a staggered shell checks it before spawning. */
    this.generation = 0;

    // Pools are sized to the worst case each effect can actually hit. A 9-cell
    // Great Pyramid has a 9-edge outline and a big combo can clear most of a
    // face at once, so the borders and the shatter get the deep pools; the
    // hover effects only ever cover the 1-3 cells of a piece.
    //
    // A tinted effect pays this per tone, not once — `Effect` registers a variant
    // for each, so the fireworks' depth is per *colour*, not per salvo. A combo
    // fires one shell per formation and walks five tones, so a twelve-formation
    // move puts at most three shells on any one tone; each lives about 2.2s and
    // they are 260ms apart, so three is also the most that can be in the air
    // together. Four leaves a slot in hand.
    this.gemSparkle = new Effect(this.system, gemSparkleDef, { maxInstances: 20 });
    this.pieceLandPoof = new Effect(this.system, pieceLandPoofDef, { maxInstances: 6 });
    this.hoverEdgeGlow = new Effect(this.system, hoverEdgeGlowDef, { maxInstances: 10 });
    this.hoverAurora = new Effect(this.system, hoverAuroraDef, { maxInstances: 2 });
    this.formationEdgeGlow = new Effect(this.system, formationEdgeGlowDef, { maxInstances: 16 });
    this.clearShatter = new Effect(this.system, clearShatterDef, { maxInstances: 14 });
    this.comboFireworks = new Effect(this.system, comboFireworksDef, { maxInstances: 4 });
    // One per formation announced, and the announcements are ~200ms apart while
    // the sparks live over a second, so a long combo genuinely stacks these.
    this.apexBeam = new Effect(this.system, apexBeamDef, { maxInstances: 4 });
    // The one permanent resident: a single looping instance, up for the whole
    // game. It still lives in a pooled Effect so reset() can dispose it with
    // the rest; #igniteFireflies puts it back.
    this.ambientFireflies = new Effect(this.system, ambientFirefliesDef, { maxInstances: 1 });

    this.effects = [
      this.gemSparkle,
      this.pieceLandPoof,
      this.hoverEdgeGlow,
      this.hoverAurora,
      this.formationEdgeGlow,
      this.clearShatter,
      this.comboFireworks,
      this.apexBeam,
      this.ambientFireflies,
    ];

    /** Cached per-face orientations — three of them, and they never change. */
    this.faceQuats = [0, 1, 2].map(faceQuaternion);

    /**
     * When each stone next twinkles, in `performance.now()` ms. Seeded across
     * the whole window so the opening board does not sparkle all at once.
     */
    this.nextSparkle = new Float64Array(CELL_COUNT);
    this.resetSparkleClocks(0);

    // The fireflies go up with the system and stay up; see #igniteFireflies.
    this.#igniteFireflies();

    /** Live looping instances belonging to the current hover, to be stopped. */
    this.hoverInstances = [];
    this.hoverKey = '';

    /**
     * Called whenever a stone twinkles, so something else can make a noise
     * about it. Set by the caller; nothing here knows about audio.
     * @type {(() => void) | null}
     */
    this.onSparkle = null;
  }

  /**
   * Light the ambient fireflies: one looping instance of barely-there gold
   * flecks drifting in a deep box around the whole board — in front of it and
   * behind it, since the box spans the pyramid in depth and the particles sort
   * against it.
   *
   * Spawned at the centre of the board's bounding box with the board's own
   * identity frame rather than a face or skyward quaternion: the effect is
   * simulated in world space (its meta says so), so each fleck is born where
   * the box happens to be and then drifts free, unmoved by the board turning
   * under it. The box itself is near enough rotation-invariant about the spin
   * axis for its ride on the board to be invisible.
   */
  #igniteFireflies() {
    this.ambientFireflies.spawn({ position: [0, (APEX[1] + GROUND_Y) / 2, 0] });
  }

  resetSparkleClocks(now) {
    for (let id = 0; id < CELL_COUNT; id += 1) {
      this.nextSparkle[id] = now + SPARKLE_MIN_MS + Math.random() * (SPARKLE_MAX_MS - SPARKLE_MIN_MS);
    }
  }

  /* ---------------------------------------------------------------- ambient */

  /**
   * Twinkle the stones, then advance the simulation.
   *
   * Only stones on a face that is actually turned toward the camera are
   * considered: a sparkle behind the pyramid is particles spent on nothing,
   * and there are two hidden faces at any moment. Cells that are not eligible
   * still have their clocks pushed forward, so a face coming round does not
   * arrive owing a sparkle from every stone on it at once.
   *
   * @param {number} dt seconds since the last frame
   * @param {number} now `performance.now()`
   * @param {(id: number) => boolean} isLit stone showing, on a visible face
   */
  update(dt, now, isLit) {
    for (let id = 0; id < CELL_COUNT; id += 1) {
      if (now < this.nextSparkle[id]) continue;
      this.nextSparkle[id] =
        now + SPARKLE_MIN_MS + Math.random() * (SPARKLE_MAX_MS - SPARKLE_MIN_MS);
      if (!isLit(id)) continue;

      const cell = CELLS[id];
      this.gemSparkle.spawn({
        position: lifted(cellCentroid(id), faceBasis(cell.face).normal, FACE_LIFT),
        quaternion: this.faceQuats[cell.face],
        scale: 0.85,
      });
      this.onSparkle?.();
    }

    this.system.update(dt);
  }

  /* ------------------------------------------------------------------ hover */

  /**
   * Dress the cells a carried piece would drop into: a wide glow spilling out
   * of every edge of the outline, and a sparkle aurora over the space inside
   * it.
   *
   * These are the only looping effects in the game, so they are the only ones
   * that have to be switched off again. Keyed on the target so that the common
   * case — a pointer move that leaves the target where it was — costs a string
   * compare rather than a teardown and rebuild, which would restart the loops
   * every frame and show as a stutter along the border.
   */
  setHover(cells, tone = 0) {
    const key = cells?.length ? `${tone}:${[...cells].sort((a, b) => a - b).join(',')}` : '';
    if (key === this.hoverKey) return;
    this.hoverKey = key;
    this.clearHover(false);
    if (!key) return;

    const normal = faceBasis(CELLS[cells[0]].face).normal;

    for (const edge of outlineEdges(cells)) {
      this.hoverInstances.push(
        this.hoverEdgeGlow.spawn(
          {
            position: lifted(edge.mid, normal, EDGE_LIFT),
            quaternion: edgeQuaternion(edge),
          },
          tone,
        ),
      );
    }

    const { centre, radius } = cellsExtent(cells);
    this.hoverInstances.push(
      this.hoverAurora.spawn(
        {
          position: lifted(centre, normal, FACE_LIFT),
          quaternion: this.faceQuats[CELLS[cells[0]].face],
          // The aurora is authored over roughly one cell; open it out to cover
          // however much of the face this particular piece is claiming.
          scale: 0.75 + radius * 0.75,
        },
        tone,
      ),
    );
  }

  /**
   * Let the hover effects go. `stop()` rather than a hard kill: emission ends
   * and the particles already in the air finish their lives, so the border
   * fades out instead of vanishing mid-frame.
   */
  clearHover(resetKey = true) {
    for (const instance of this.hoverInstances) instance?.stop();
    this.hoverInstances.length = 0;
    if (resetKey) this.hoverKey = '';
  }

  /* ------------------------------------------------------------------ moves */

  /** A piece settling onto the face. One poof per stone, in the piece's tone. */
  pieceLanded(cells, tone = 0) {
    if (!cells?.length) return;
    const normal = faceBasis(CELLS[cells[0]].face).normal;
    for (const id of cells) {
      this.pieceLandPoof.spawn(
        {
          position: lifted(cellCentroid(id), normal, FACE_LIFT),
          quaternion: this.faceQuats[CELLS[id].face],
          scale: 0.8,
        },
        tone,
      );
    }
  }

  /**
   * A completed formation taking its turn in the announcement: a glowing
   * particle border on every edge of *this* formation's outline, and nothing
   * on the shapes either side of it in the sequence.
   */
  formationCleared(cells) {
    if (!cells?.length) return;
    const normal = faceBasis(CELLS[cells[0]].face).normal;
    for (const edge of outlineEdges(cells)) {
      this.formationEdgeGlow.spawn({
        position: lifted(edge.mid, normal, EDGE_LIFT),
        quaternion: edgeQuaternion(edge),
      });
    }
  }

  /** The stones going: each one shatters in its own colour. */
  stonesShattered(tonesByCell) {
    for (const [id, tone] of tonesByCell) {
      const cell = CELLS[id];
      this.clearShatter.spawn(
        {
          position: lifted(cellCentroid(id), faceBasis(cell.face).normal, FACE_LIFT),
          quaternion: this.faceQuats[cell.face],
        },
        tone,
      );
    }
  }

  /**
   * The quaternion that makes an effect's +Y point at the sky.
   *
   * Everything else here is aimed at a *face*, because everything else happens on
   * one. The rockets are the exception: they go up, and "up" is the scene's up,
   * not the slope's. The system hangs off the board group, so that direction has
   * to be expressed in the board's own frame — hence the inverse of the board's
   * world rotation.
   *
   * Taken once, at spawn. The launch direction is only read at birth, so a board
   * turned mid-flight leaves the rockets going the way they were already going,
   * which is what a rocket does.
   */
  #skywardQuaternion() {
    this.parent.updateWorldMatrix(true, false);
    this.parent.matrixWorld.decompose(scratchPos, scratchQuat, scratchScale);
    return scratchQuat.invert();
  }

  /**
   * The sparks under the beam out of the tip: a flash on the apex, a shock ring
   * off it, and rainbow motes climbing the column.
   *
   * The second effect in the game aimed skyward rather than at a face, and for
   * the same reason as the fireworks — what comes out of the tip goes *up*, and
   * up is the scene's up whichever way the board happens to be turned. The column
   * of light itself is not here: it is a shader on one quad, in `beam.js`, since
   * a solid shaft is the one thing particles are bad at.
   *
   * @param {number} power 0..1 from `beamShape` — how big the completion was
   */
  apexBurst(power = 0.5) {
    this.apexBeam.spawn({
      position: [APEX[0], APEX[1] + APEX_LIFT, APEX[2]],
      quaternion: this.#skywardQuaternion().clone(),
      // Authored for a middling clear; opened out or reined in from there, so a
      // 9-cell finish throws sparks noticeably wider than a 3x Bar.
      scale: 0.7 + power * 0.8,
    });
  }

  /**
   * Fireworks over a move that closed more than one formation: **one shell per
   * formation**, so the display counts out what the move actually did. A double is
   * two, a five-formation move is five, and the sound is spawned off the same
   * count in `main.js`.
   *
   * Each is launched off the ground in front of the formation rather than fired out
   * of the face — a firework that starts where it explodes is just a burst, and the
   * climb is most of what makes it read as one.
   *
   * One tone per shell, dealt from a deck reshuffled for every salvo — so a combo is
   * a different arrangement each time, while five shells still cover the palette
   * exactly once and no two in a row match. The burst used to draw from a five-stop
   * palette per particle, so every firework was the same rainbow and none had a
   * colour of its own.
   */
  combo(cells, count) {
    if (count < FIREWORK_COMBO || !cells?.length) return;
    const { centre } = cellsExtent(cells);
    const face = CELLS[cells[0]].face;
    const basis = faceBasis(face);
    const skyward = this.#skywardQuaternion().clone();
    // Half the width of the fan of pads, for normalising the lean.
    const reach = ((count - 1) / 2) * PAD_GAP || 1;
    const tones = fireworkToneSequence(count);

    for (let i = 0; i < count; i += 1) {
      const tone = tones[i];

      // Pads fanned either side of the formation's centre, so a salvo goes up
      // across the base rather than all out of one hole.
      const across = (i - (count - 1) / 2) * PAD_GAP;
      const pad = lifted(
        [centre[0] + basis.right[0] * across, GROUND_Y, centre[2] + basis.right[2] * across],
        basis.normal,
        LAUNCH_STANDOFF,
      );

      // And each leans the way it is already going. `skyward` puts the effect's
      // axes on the world's, so this is a tilt about world Z — which is across the
      // screen, the direction a fan wants to open in. Negative because a positive
      // turn about +Z takes +Y toward -X, and a shell to the right should lean
      // right.
      const jitter = (Math.random() * 2 - 1) * FAN_JITTER;
      const lean = -(across / reach) * FAN + jitter;
      const quaternion = skyward
        .clone()
        .multiply(scratchQuat.setFromAxisAngle(scratchZ.set(0, 0, 1), lean));

      // The first goes now so the celebration starts on the beat; the rest follow.
      if (i === 0) this.comboFireworks.spawn({ position: pad, quaternion }, tone);
      else {
        this.#later(i * STAGGER_MS, () => {
          this.comboFireworks.spawn({ position: pad, quaternion }, tone);
        });
      }
    }
  }

  /**
   * Run `fn` later, unless the board has been reset in the meantime.
   *
   * A salvo outlives the move that fired it, so a restart part-way through would
   * otherwise drop shells onto a board that no longer exists — and `reset()` has
   * just disposed the pools they would spawn from.
   */
  #later(delay, fn) {
    const generation = this.generation;
    setTimeout(() => {
      if (generation === this.generation) fn();
    }, delay);
  }

  /* ------------------------------------------------------------------ admin */

  /**
   * Abandon everything mid-flight, for a restart.
   *
   * Per *asset*, not `VFXSystem.dispose()` — that one unregisters the effects
   * and detaches the whole system from the scene, which is teardown, not a
   * reset, and would leave every later spawn silently doing nothing. Disposing
   * an asset empties its instance pool and hands the GPU buffers back; the
   * next spawn rebuilds one.
   */
  reset(now = 0) {
    // Before the pools go: any shell still queued from the last game checks this
    // and stands down rather than spawning from a disposed asset.
    this.generation += 1;
    this.clearHover();
    for (const effect of this.effects) {
      for (const asset of effect.variants) asset.dispose();
    }
    this.resetSparkleClocks(now);
    // Everything above is one-shot and simply gone; the fireflies are the one
    // resident loop, so a reset has to light them again.
    this.#igniteFireflies();
  }

  stats() {
    return this.system.stats?.() ?? null;
  }
}
